//
// Copyright (c) 2021 Astute Global, Inc.
//

// @ts-ignore
import i18next from "i18n/i18n";
import TemplatePreviewView from "./templatePreviewView";
import {
  addListToDropdown,
  ButtonView,
  Collection,
  createLabeledDropdown,
  CssTransitionDisablerMixin,
  IconCancel,
  IconCheck,
  LabeledFieldView,
  submitHandler,
  View,
  ViewModel,
} from "ckeditor5";
import { removeCkReset } from "../../ckEditorUtil";
import Messenger from "messaging/messenger";

import "../css/commonTemplateView.css";
import "../css/insertTemplateView.css";

class BaseInsertTemplateView extends View {
  constructor(editor, command) {
    super(editor.locale);
    this.editor = editor;
    this.command = command;

    this.set({
      templatesToDelete: [],
      templateId: null,
      templateName: null,
      templateHtml: null,
    });

    this.instructions = this._createInstructions();
    this.chooseTemplateView = this._createChooseTemplateView();
    this.templatePreview = this._createTemplatePreview();
    this.okButton = this._createOkButton();
    this.deleteButton = this._createDeleteButton();
    this.cancelButton = this._createCancelButton();

    this.setTemplate({
      tag: "div",
      attributes: {
        class: [
          "ck",
          "ck-responsive-form",
          "ck-vertical-form",
          "custom-template-view",
          "custom-template-insert-view",
        ],
      },
      children: this._createFormChildren(),
    });
  }

  focus() {
    if (
      this.chooseTemplateView &&
      this.chooseTemplateView.fieldView &&
      this.chooseTemplateView.fieldView
    ) {
      this.chooseTemplateView.fieldView.focus();
    }
  }

  render() {
    super.render();
    // Remove the CKEditor reset CSS classes to better integrate with Marionette UI
    setTimeout(() => removeCkReset(this.element), 100);
    submitHandler({
      view: this,
    });
  }

  reset() {
    this.set({
      templateId: null,
      templateName: null,
      templateHtml: null,
      templatesToDelete: [],
    });
    this.allTemplates = Messenger.reqres.request("custom:templates");
    this._syncTemplateModels(
      this.chooseTemplateView.fieldView,
      this.allTemplates,
      this.chooseTemplateModels,
    );
  }

  _createCancelButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.cancel"),
      icon: IconCancel,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-cancel",
      },
    });
    button.delegate("execute").to(this, "cancel");
    return button;
  }

  _createChooseTemplateView() {
    const labeledFieldView = new LabeledFieldView(this.locale, createLabeledDropdown);
    labeledFieldView.set({
      class: "insert-template-template-wrapper",
      label: i18next.t("ckeditor.customTemplate.templates"),
    });
    labeledFieldView.bind("isEmpty").to(this, "templateId", (value) => !value);
    const dropdownView = labeledFieldView.fieldView;
    dropdownView.class = "insert-template-template-select";
    dropdownView.buttonView.bind("label").to(this, "templateName");
    dropdownView.buttonView.withText = true;
    dropdownView.on("execute", (evt) => {
      const eventSource = evt.source;
      this.set({
        templateId: eventSource.id,
        templateName: eventSource.label,
        templateHtml: eventSource.templateHtml,
      });
    });
    this.chooseTemplateModels = new Collection();
    addListToDropdown(dropdownView, this.chooseTemplateModels);
    // The actual binding of the Custom Template collection to the Models will happen later, when reset() is called
    return labeledFieldView;
  }

  _createDeleteButton() {
    const button = new ButtonView(this.locale);
    button.set({
      class: "delete-template",
      label: i18next.t("ckeditor.customTemplate.deleteTemplate"),
      tooltip: true,
      withText: true,
    });
    button.on("execute", () => this._onDeleteTemplate());
    return button;
  }

  _createFormChildren() {
    const collection = this.createCollection();
    collection.add(this.instructions);
    collection.add(this.chooseTemplateView);
    collection.add(this.templatePreview);
    if (Messenger.reqres.request("can:delete:customTemplate")) {
      collection.add(this.deleteButton);
    }
    collection.add(this.okButton);
    collection.add(this.cancelButton);
    return collection;
  }

  _createInstructions() {
    const instructionsView = new View(this.locale);
    instructionsView.setTemplate({
      tag: "div",
      attributes: {
        class: "sub-header",
      },
      children: [
        {
          text: i18next.t("ckeditor.customTemplate.createInstructions"),
        },
      ],
    });
    return instructionsView;
  }

  _createOkButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.ok"),
      icon: IconCheck,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-save",
      },
    });
    button.type = "submit";
    button.delegate("execute").to(this, "submit");
    return button;
  }

  _createTemplatePreview() {
    const labeledFieldView = new LabeledFieldView(this.locale, (parentView, viewUid, statusUid) => {
      const view = new TemplatePreviewView(this.locale, viewUid, statusUid);
      view.bind("rawHtml").to(this, "templateHtml");
      return view;
    });
    labeledFieldView.class = "template-preview-field";
    labeledFieldView.label = i18next.t("ckeditor.customTemplate.templatePreview");
    labeledFieldView.isEmpty = false;
    return labeledFieldView;
  }

  _onDeleteTemplate() {
    if (this.templateId) {
      const template = this.allTemplates.get(this.templateId);
      if (template) {
        this.templatesToDelete.push(template);
        this.allTemplates.remove(template);
        const model = this.chooseTemplateModels.find(({ model }) => model.id == template.id);
        if (model) {
          this.chooseTemplateModels.remove(model);
        }
        // Always clear selection after deletion
        this.set({
          templateId: null,
          templateName: null,
          templateHtml: null,
        });
      }
    }
  }

  _syncTemplateModels(listFieldView, templates, models) {
    templates.onSynced(() => {
      if (templates.length === 0) {
        models.clear();
        this.set({
          templateId: null,
          templateName: null,
          templateHtml: null,
        });
      } else {
        models.clear();
        templates.each((template) => {
          const model = new ViewModel({
            label: template.get("name"),
            id: template.id,
            templateHtml: template.get("template"),
            tooltip: true,
            withText: true,
          });
          model.bind("isOn").to(this, "templateId", (id) => id == template.id);
          models.add({
            type: "button",
            model: model,
          });
        });
        const firstTemplate = templates.at(0);
        this.set({
          templateId: firstTemplate.id,
          templateName: firstTemplate.get("name"),
          templateHtml: firstTemplate.get("template"),
        });
      }
    });
  }
}

const InsertTemplateView = CssTransitionDisablerMixin(BaseInsertTemplateView);

export default InsertTemplateView;
